import { useEffect, useRef } from 'react'
import { playExclusiveBackgroundMusic, releaseBackgroundMusic } from '../../../common/backgroundMusicBus'

interface UseAudioPlayerProps {
  backgroundMusicEnabled: boolean
  musicVolume: number
}

export const useAudioPlayer = ({ backgroundMusicEnabled, musicVolume }: UseAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    let cancelled = false

    const playAudio = async () => {
      const audio = audioRef.current
      if (!audio) return

      try {
        if (audio.readyState < 2) {
          await new Promise<void>((resolve) => {
            audio.addEventListener('canplaythrough', () => resolve(), { once: true })
            audio.addEventListener('error', () => resolve(), { once: true })
            audio.load()
          })
        }
        if (cancelled || audioRef.current !== audio) return

        audio.volume = musicVolume
        if (backgroundMusicEnabled) {
          await playExclusiveBackgroundMusic(audio)
        } else {
          audio.pause()
          releaseBackgroundMusic(audio)
        }
      } catch (error) {
        if (!cancelled) console.error('Autoplay prevented or error:', error)
      }
    }

    const handleUserInteraction = () => {
      playAudio()
    }

    document.addEventListener('click', handleUserInteraction, { once: true })
    document.addEventListener('keydown', handleUserInteraction, { once: true })
    document.addEventListener('touchstart', handleUserInteraction, { once: true })

    playAudio()

    return () => {
      cancelled = true
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
      document.removeEventListener('touchstart', handleUserInteraction)
    }
  }, [backgroundMusicEnabled, musicVolume])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = musicVolume
    }
  }, [musicVolume])

  return audioRef
}
